"use client";

import Link from "next/link";
import Image from "next/image";
import { trackEvent } from '@/app/lib/posthog';

const linkClassName =
  "hover:text-red-600 dark:hover:text-red-400 transition-colors";

export default function Footer() {
    return (
        <footer className="py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 transition-colors duration-300">
            <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-8 mb-8">
                    <div>
                        <div className="flex items-center mb-4">
                            <span className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                                <Image
                                    src="/logo/scarlet-sync-no-bg.png"
                                    alt="Scarlet Sync Logo"
                                    width={24}
                                    height={24}
                                    className="h-6 w-6 object-contain"
                                />
                                <span>Scarlet Sync</span>
                            </span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">
                            Public Rutgers course, campus, and planning data for students and agents.
                        </p>
                    </div>
                    <div>
                        <h3 className="font-semibold mb-4 text-slate-900 dark:text-white">Discover</h3>
                        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                            <li><Link href="/courses" className={linkClassName}>Course Wiki</Link></li>
                            <li><Link href="/professors" className={linkClassName}>Professors</Link></li>
                            <li><Link href="/explore" className={linkClassName}>Course Explorer</Link></li>
                            <li><Link href="/sirs" className={linkClassName}>SIRS Ratings</Link></li>
                            <li><Link href="/resources" className={linkClassName}>Student resources</Link></li>
                            <li><Link href="/research-opportunities" className={linkClassName}>Research Opportunities</Link></li>
                            <li><Link href="/about/data" className={linkClassName}>About this data</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-semibold mb-4 text-slate-900 dark:text-white">Campus</h3>
                        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                            <li><Link href="/campus" className={linkClassName}>Campus hub</Link></li>
                            <li><Link href="/map" className={linkClassName}>Campus map</Link></li>
                            <li><Link href="/dining" className={linkClassName}>Dining</Link></li>
                            <li><Link href="/parking" className={linkClassName}>Parking</Link></li>
                            <li><Link href="/parking-map" className={linkClassName}>Parking Map</Link></li>
                            <li><Link href="/bus-tracker" className={linkClassName}>Bus Tracker</Link></li>
                            <li><Link href="/rooms" className={linkClassName}>Classroom guide</Link></li>
                            <li><Link href="/events" className={linkClassName}>Campus Events</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-semibold mb-4 text-slate-900 dark:text-white">Planning</h3>
                        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                            <li><Link href="/scheduling" className={linkClassName}>Schedule Builder</Link></li>
                            <li><Link href="/scheduling/alerts" className={linkClassName}>Course Sniper</Link></li>
                            <li><Link href="/planning" className={linkClassName}>Plan</Link></li>
                            <li><Link href="/degree-planning" className={linkClassName}>Degree Planning</Link></li>
                            <li><Link href="/gpa-calculator" className={linkClassName}>GPA calculator</Link></li>
                            <li><Link href="/classes-by-time" className={linkClassName}>Classes By Time</Link></li>
                            <li><Link href="/classes-on-map" className={linkClassName}>Classes On Map</Link></li>
                            <li><Link href="/room-availability" className={linkClassName}>Room Availability</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-semibold mb-4 text-slate-900 dark:text-white">Rutgers Guides</h3>
                        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                            <li><Link href="/guides" onClick={() => trackEvent('content:guide_navigation_clicked', { destination: 'guides', placement: 'footer' })} className={linkClassName}>All planning guides</Link></li>
                            <li><Link href="/guides/how-to-build-a-rutgers-schedule" onClick={() => trackEvent('content:guide_navigation_clicked', { destination: 'schedule_guide', placement: 'footer' })} className={linkClassName}>Build a schedule</Link></li>
                            <li><Link href="/guides/rutgers-new-brunswick-transfer-student-schedule" onClick={() => trackEvent('content:guide_navigation_clicked', { destination: 'transfer_guide', placement: 'footer' })} className={linkClassName}>Transfer checklist</Link></li>
                            <li><Link href="/guides/rutgers-commuter-schedule-planning" onClick={() => trackEvent('content:guide_navigation_clicked', { destination: 'commuter_guide', placement: 'footer' })} className={linkClassName}>Commuter planning</Link></li>
                            <li><Link href="/guides/sources" className={linkClassName}>Source review status</Link></li>
                            <li><Link href="/finals" className={linkClassName}>Final exam grids</Link></li>
                            <li><Link href="/ontology" className={linkClassName}>Ontology Graph</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-semibold mb-4 text-slate-900 dark:text-white">Help</h3>
                        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                            <li><Link href="/help" className={linkClassName}>Help hub</Link></li>
                            <li><Link href="/support" className={linkClassName}>Support</Link></li>
                            <li><Link href="/developers" className={linkClassName}>Developers</Link></li>
                            <li><Link href="/ai-assistant" className={linkClassName}>Ask Scarlet Sync</Link></li>
                            <li><Link href="/login" className={linkClassName}>Login</Link></li>
                            <li><Link href="/profile" className={linkClassName}>Profile</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-slate-200 dark:border-slate-700 pt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="text-slate-600 dark:text-slate-400 text-sm mb-4 md:mb-0">
                            © {new Date().getFullYear()} Scarlet Sync. All rights reserved.
                        </div>
                        <div className="flex gap-4 text-sm">
                            <Link href="/contact" className="text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">Contact Us</Link>
                            <Link href="/privacy-policy" className="text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">Privacy Policy</Link>
                            <Link href="/tos" className="text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">Terms of Service</Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
